import * as React from "react";
import styled from "@emotion/styled";
import { Text } from "atoms";
import { useAuthHasRole } from "features/auth";
import { sortReviewsByCreated, sortReviewsByUpvotes } from "../utils/sorting";
import { Review } from "./Review";
import { useReviewVotes } from "../api/useReviewVotes";
import type { ReviewSortOption } from "./ReviewSort";
import type { ArtistReview, ProjectReview, ReviewVote } from "../types";

interface ReviewListProps {
  entityType: "ARTIST" | "PROJECT";
  entityId: string;
  sortBy: ReviewSortOption;
  reviews: (ArtistReview | ProjectReview)[];
}

function ReviewList({
  reviews,
  entityId,
  entityType,
  sortBy,
  ...props
}: ReviewListProps) {
  const { data: reviewVotes } = useReviewVotes({
    reviewableId: entityId,
    reviewableType: entityType,
  });

  const isModerator = useAuthHasRole(["curator-level-2", "curator-level-3"]);

  // Filter reviews according to user's role and then sort according to their sort selection
  const sortedReviews = React.useMemo(() => {
    const visibleStatuses = isModerator
      ? ["PENDING", "PUBLISHED"]
      : ["PUBLISHED"];

    const filtered = reviews.filter((r) => visibleStatuses.includes(r.status));

    if (sortBy === "created") {
      return filtered.sort(sortReviewsByCreated);
    }
    if (sortBy === "created desc") {
      return filtered.sort(sortReviewsByCreated).reverse();
    }

    return filtered.sort(sortReviewsByUpvotes);
  }, [sortBy, reviews, isModerator]);

  // Transform array of user's review votes into a map keyed by reviewid
  const reviewVotesMap = React.useMemo(() => {
    if (!reviewVotes) return {};

    return reviewVotes.reduce(
      (acc, cur) => ({
        ...acc,
        [cur.reviewId]: cur,
      }),
      {} as { [key: string]: ReviewVote }
    );
  }, [reviewVotes]);

  return (
    <section {...props}>
      {sortedReviews ? (
        sortedReviews.map((review) => (
          <Review
            key={review.reviewId}
            review={review}
            usersVote={reviewVotesMap[review.reviewId]}
            activeSort={sortBy}
          />
        ))
      ) : (
        <P>This project has no reviews, you should write one!</P>
      )}
    </section>
  );
}

const P = styled(Text)({
  marginTop: "2rem",
  fontStyle: "italic",
});

export { ReviewList };
